import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const saintLaurentDuVarEn: ChauffeurPage = {
  slug: "saint-laurent-du-var",
  title: "VTC & private chauffeur in Saint-Laurent-du-Var",
  subtitle:
    "Private chauffeur in Saint-Laurent-du-Var: express Nice airport shuttle, CAP3000, the marina and the beaches.",
  keywords: [
    "vtc saint-laurent-du-var",
    "private chauffeur saint-laurent-du-var",
    "nice airport shuttle saint-laurent",
  ],
  hero: {
    title: "VTC & private chauffeur in Saint-Laurent-du-Var",
    subtitle:
      "Right by Nice airport: express shuttle (~8 min), CAP3000, the marina and the banks of the Var — available 24/7.",
    keywords: [
      "VTC Saint-Laurent-du-Var",
      "Nice airport shuttle",
      "CAP3000 chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "VTC & private chauffeur in Saint-Laurent-du-Var, next to Nice airport",
    badges: [
      "Express airport shuttle",
      "Flight tracking",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Saint-Laurent-du-Var",
    items: [
      {
        title: "Nice airport in ~8 min",
        description:
          "The neighbouring commune to the airport, split off only by the Var: an express shuttle both ways, flight tracking and a terminal welcome.",
      },
      {
        title: "CAP3000 & marina",
        description:
          "One of France's largest shopping centres and the restaurant-lined marina: drop-off as close as possible.",
      },
      {
        title: "Banks of the Var & beaches",
        description:
          "The Var riverbanks, the promenade and the beaches: seafront trips with no parking worry.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Saint-Laurent-du-Var",
    services: [
      {
        title: "Express Nice airport shuttle",
        text: "Saint-Laurent-du-Var ⇄ Nice Côte d'Azur airport in about 8 minutes, ideal for early flights and tight connections, with flight tracking.",
      },
      {
        title: "CAP3000",
        text: "Drop-off and pick-up at the foot of the shopping centre, even loaded with luggage or shopping.",
      },
      {
        title: "Saint-Laurent-du-Var marina",
        text: "A link to the marina and its restaurants, late-night returns included.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or by the day, a dedicated chauffeur for your trips along the Riviera.",
      },
      {
        title: "Long distance & Riviera links",
        text: "Departures from Saint-Laurent to Cannes, Monaco, Italy or the ski resorts, setting off right by the airport.",
      },
    ],
  },
  locations: {
    heading: "Areas & pick-up points",
    items: [
      {
        name: "CAP3000",
        description: "Large shopping centre on the seafront near the airport.",
      },
      {
        name: "Saint-Laurent-du-Var marina",
        description: "Marina and seafront restaurants.",
      },
      {
        name: "Banks of the Var",
        description: "Riverside promenade facing Nice.",
      },
      {
        name: "Beaches & promenade",
        description: "Seafront and beaches of the commune.",
      },
      {
        name: "Saint-Laurent-du-Var station",
        description: "Rail link and interchange hub.",
      },
      {
        name: "Nice airport",
        description: "~8 min, on the other side of the Var.",
      },
    ],
  },
  generic: {
    heading: "Saint-Laurent-du-Var, one bridge from Nice airport",
    subheading:
      "Split from the airport only by the Var, the ideal town for an express shuttle and shopping at CAP3000.",
    paragraphs: [
      "Saint-Laurent-du-Var sits right beside Nice airport: only the river Var separates the commune from the runways. It is the perfect base for an express shuttle — allow about 8 minutes to the terminals — invaluable for an early flight, a group departure or a tight connection.",
      "Along the shore, CAP3000, one of France's largest shopping centres, sits next to the marina and its restaurants. The chauffeur drops you as close as possible and collects you loaded with luggage or shopping, with no hunt for a space.",
      "Between the banks of the Var, the beaches and the promenade, Saint-Laurent is also a handy departure point for the whole Riviera: Cannes, Monaco, Italy or the ski resorts, setting off right by the airport, with flight tracking and a personal welcome.",
    ],
    bullets: [
      "Airport shuttle ~8 min",
      "CAP3000 & marina",
      "Riviera departures right by the airport",
    ],
  },
  process: {
    heading: "Book a chauffeur in Saint-Laurent-du-Var",
    steps: [
      "Share your flight or your addresses (airport, CAP3000, marina, beaches).",
      "We confirm the right vehicle and the fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you on time, even very early.",
      "Express shuttle, drop-off at the marina or CAP3000, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Saint-Laurent-du-Var"),
    {
      q: "How long is the drive to Nice airport from Saint-Laurent-du-Var?",
      a: "Only about 8 minutes: the commune neighbours the airport, on the other side of the Var. Ideal for an early flight or a tight connection, and we track your flight both ways.",
    },
    {
      q: "Can I book a last-minute shuttle to the airport?",
      a: "Yes, the immediate proximity of the airport makes last-minute requests easier subject to availability; still, book ahead for very early flights and in high season.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer in Saint-Laurent-du-Var?",
      a: "Mercedes E-Class and S-Class sedans, a 7-seat van, SUV and VIP minibus, depending on the number of passengers and the amount of luggage.",
    },
    {
      q: "Do you serve CAP3000?",
      a: "Yes, we drop off and collect at the foot of the CAP3000 shopping centre, even loaded with luggage or shopping, with no need to look for a space.",
    },
    {
      q: "Do you drive to the marina and its restaurants?",
      a: "Yes, we link the Saint-Laurent-du-Var marina and its restaurants, with relaxed late-night returns.",
    },
  ],
  relatedLinks: [
    { label: "Book an airport transfer", href: "/en/chauffeur/airport" },
    { label: "Private chauffeur in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Transfers across the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Car service in Cannes", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Saint-Laurent-du-Var",
    text: "Express airport shuttle, CAP3000 or marina — quick reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Saint-Laurent-du-Var — private chauffeur",
    description:
      "Private car service in Saint-Laurent-du-Var: express Nice airport shuttle (~8 min), CAP3000, the marina and the beaches. 5.0/5 from 38 Google reviews.",
    canonical: "/en/chauffeur/saint-laurent-du-var",
  },
};

export default saintLaurentDuVarEn;
